
import { defineComponent } from "vue";
import { Modal } from "bootstrap";
import axios from "@/axios-instance";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import moment from "moment";
import { titleCaseName } from "@/core/helpers/format";
import { sortByAlphabet } from "@/core/helpers/sortHelpers";
import { reFormatCurrent } from "@/views/apps/procedureSettings/scholarship.vue";
import Permission from "@/core/config/Permission";
import { checkRoleIsAdmin } from "@/router";
import { filterScholarshipOptions, canViewCandidateDetail } from "@/core/helpers/profileAccess";

export default defineComponent({
  name: "scholarship-approve-modal",
  emits: ["saved"],
  data() {
    return {
      Permission,
      dataCustomer: { fullName: "" } as any,
      arrScholarshipA: [
        { issuedScholarshipId: "", issuedScholarshipValue: "", programtype: "", issuedMajorId: "" },
      ] as any[],
      rules: {} as any,
      saving: false, // chống double-click: duyệt/cấp học bổng (createmulti) là LUỒNG TIỀN, bấm 2 lần = cấp TRÙNG
      isMultiApproved: false,
      multiCount: 0,
      scholarshipOptions: [] as any[],
      majorOptions: [] as any[],
      admissionMajorOptions: [] as any[], // ngành ứng viên ĐÃ ĐK tuyển sinh (kèm admissionRegisterId+MHS) -> lọc "Ngành nhận HB"
      programTypeLabel: { TIENG_VIET: "Tiếng Việt", SONG_NGU: "Song ngữ" } as any,
    };
  },
  computed: {
    // Mirror ĐÚNG điều kiện v-permission(scholarship/APPROVE) của VÙNG 2 (kể cả nhánh admin auto-pass).
    // v-permission chỉ display:none (không gỡ DOM) -> el-form-item ẩn VẪN đăng ký validate; nếu require issued*
    // cho user không có quyền thì bấm Lưu bị chặn im lặng trên field vô hình (bug "không thấy phần Quyết định, lưu không được").
    canApprove(): boolean {
      if (checkRoleIsAdmin()) return true;
      const temp = window.localStorage.getItem("user");
      const user = temp ? JSON.parse(temp) : ({} as any);
      const feature = user?.permissions?.find((i: any) => i.name == Permission.SCHOLARSHIP);
      return !!(feature?.permissions instanceof Array && feature.permissions.includes(Permission.SCHOLARSHIP_APPROVE));
    },
  },
  created() {
    this.loadOptions();
  },
  methods: {
    show() {
      const el = this.$refs.modalRef as HTMLElement | null;
      if (el) (Modal.getInstance(el) || new Modal(el)).show(); // bootstrap 5.0.1 chưa có getOrCreateInstance
    },
    hide() {
      const el = this.$refs.modalRef as HTMLElement | null;
      if (el) Modal.getInstance(el)?.hide();
    },
    loadOptions() {
      axios.get(`/setting/scholarship`)
        // HB gắn cờ leaderonly (vd LIBERAL_v2) chỉ trưởng nhóm/admin thấy trong dropdown cấp
        .then(({ data }) => { this.scholarshipOptions = filterScholarshipOptions(sortByAlphabet(data, 'name')); })
        .catch(({ response }) => { console.log('err', response); });
      axios.get(`/setting/tuition`)
        .then(({ data }) => {
          const list: any[] = [];
          sortByAlphabet(data, 'majorName').forEach((item: any) => {
            list.push({
              code: item.id,
              name: item.majorName + ' - ' + this.programTypeLabel[item.programtype],
              majorName: item.majorName,
              majorCode: item.majorCode,
              programtype: item.programtype,
              id: item.id,
              wholeCourseFee: (+item.wholeCourseFee).toLocaleString('vi-VN'),
            });
          });
          this.majorOptions = list;
        })
        .catch(({ response }) => { console.log('err', response); });
    },
    // Mở modal: record đã map (trang DS) HOẶC id scholarship_register (chi tiết ứng viên -> tự fetch + map)
    async open(recordOrId: any) {
      let record = recordOrId;
      if (typeof recordOrId === 'string') {
        try {
          const { data: item } = await axios.get(`/scholarshipregisters/${recordOrId}?select=Student,AdmissionRegister,Scholarship`);
          if (!item || !item.id) throw new Error('not found');
          // Map về đúng shape record của trang DS (id_record = id thật)
          record = {
            id_record: item.id,
            studentId: item.Student?.id,
            opportunityid: item.Student?.opportunityid,
            initIssuedDate: item.issuedDate,
            issuedNo: item.issuedNo,
            issuedPhase: item.issuedPhase,
            issuedProgramtype: item.issuedProgramtype,
            issuedScholarshipCode: item.issuedScholarshipCode,
            otherInfo: item.otherInfo,
            issuedScholarshipName: item.issuedScholarshipName,
            issuedScholarshipValue: item.IssuedScholarship?.value ? (+item.IssuedScholarship.value).toLocaleString('vi-VN') : '',
            majorCode: item.majorCode,
            majorId: item.majorId,
            majorName: item.majorName,
            status: item.status,
            regScholarshipName: item.regScholarshipName,
            regScholarshipCode: item.regScholarshipCode,
            regScholarshipId: item.regScholarshipId,
            regScholarshipValue: item.RegScholarship?.value ? (+item.RegScholarship.value).toLocaleString('vi-VN') : '',
            admissionRegisterId: item.admissionRegisterId,
            admissionRegisterCode: item.admissionRegisterCode,
            fullName: titleCaseName(item.Student?.fullName || item.studentName),
            phone: item.Student?.phone,
            dateOfBirth: item.Student?.dateOfBirth ? moment(item.Student.dateOfBirth).format('DD/MM/YYYY') : '',
            schoolName: item.Student?.class12School?.name,
            schoolProvince: item.Student?.class12Province?.name,
            assignee: item.Student?.User?.fullName,
            // Link "Xem hồ sơ đầy đủ" chỉ khi admin/mình phụ trách — leader duyệt dùm KHÔNG mở trang ứng viên người khác
            canViewStudent: canViewCandidateDetail(item.Student?.assignee, item.Student?.groupAssignee),
          };
        } catch (e: any) {
          Swal.fire({ text: e?.response?.data?.message || 'Không tải được đăng ký học bổng.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } });
          return;
        }
      }
      this.fillFromRecord(record);
      this.show();
    },
    // Bulk "Approved (n)" của trang DS — giữ nguyên hành vi cũ (luồng bulc vốn short-circuit trong submit)
    openMulti(count: number) {
      this.rules = {
        issuedNo: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
        issuedPhase: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
        issuedScholarshipId: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
        issuedDate: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
        status: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
      };
      this.dataCustomer = {};
      setTimeout(() => {
        (this.$refs.formRef as any)?.resetFields();
        this.dataCustomer.status = 'approved';
      }, 300);
      this.isMultiApproved = true;
      this.multiCount = count;
      this.show();
    },
    // = handleClickModal cũ của registerScholarshipList (giữ nguyên logic)
    fillFromRecord(record: any) {
      this.isMultiApproved = false;
      (this.$refs.formRef as any)?.resetFields();
      this.getAdmissionMajors(record.studentId); // tải ngành ứng viên đã ĐK tuyển sinh -> nguồn "Ngành nhận HB" + admissionRegisterId
      this.dataCustomer = {
        studentId: record.studentId,
        opportunityid: record.opportunityid,
        issuedDate: record.initIssuedDate,
        issuedNo: record.issuedNo,
        issuedPhase: record.issuedPhase,
        issuedProgramtype: record.issuedProgramtype,
        issuedScholarshipCode: record.issuedScholarshipCode,

        issuedScholarshipId: record.otherInfo && record.otherInfo[0]?.issuedScholarshipId,
        issuedMajorId: record.otherInfo && record.otherInfo[0]?.issuedMajorId,

        issuedScholarshipName: record.issuedScholarshipName,
        issuedScholarshipValue: record.issuedScholarshipValue,
        majorCode: record.majorCode,
        majorId: record.majorId,
        majorName: record.majorName,
        status: record.status == 'rejected' ? 'rejected' : 'approved', // giữ 'rejected' khi mở lại hồ sơ đã từ chối (để hiện lại lý do)
        id: record.id_record,
        regScholarshipName: record.regScholarshipName,
        regScholarshipCode: record.regScholarshipCode,
        regScholarshipId: record.regScholarshipId,
        regScholarshipValue: record.regScholarshipValue,
        admissionRegisterId: record.admissionRegisterId,
        admissionRegisterCode: record.admissionRegisterCode,
        fullName: record.fullName,
        phone: record.phone,
        dateOfBirth: record.dateOfBirth,
        schoolName: record.schoolName,
        schoolProvince: record.schoolProvince,
        assignee: record.assignee,
        // record từ trang DS đã tính sẵn canViewStudent; từ chi tiết ứng viên (đang đứng trong trang đó) -> true
        canViewStudent: record.canViewStudent !== undefined ? record.canViewStudent : true,
        // Lý do từ chối lưu trong otherInfo dạng object {rejectReason}; khi Duyệt otherInfo là MẢNG học bổng-cấp
        rejectReason: (record.otherInfo && !Array.isArray(record.otherInfo)) ? record.otherInfo.rejectReason : '',
      };
      if (Array.isArray(record.otherInfo)) { // chỉ là mảng học bổng-cấp khi Duyệt; object {rejectReason} thì rơi xuống else
        this.arrScholarshipA = record.otherInfo;
        record.otherInfo?.forEach((item: any, index: number) => {
          let rateShow = this.scholarshipOptions.find(i => i.code == item.issuedScholarshipCode)?.rate;
          const currentFee = this.majorOptions.find(i => i.id == item?.issuedMajorId)?.wholeCourseFee;
          // Học phí toàn khóa: ƯU TIÊN SNAPSHOT đã lưu lúc duyệt (otherInfo[].wholeCourseFee).
          // Bảng học phí không có chiều thời gian — sửa biểu phí khóa mới sẽ GHI ĐÈ khóa cũ, nên nếu
          // đọc giá hiện tại thì hồ sơ đã duyệt năm trước mở lại sẽ hiện số của năm nay (sai quyết định đã ký).
          const snapshotFee = item.wholeCourseFee;
          const hasSnapshot = snapshotFee !== null && snapshotFee !== undefined && snapshotFee !== '' && +reFormatCurrent(snapshotFee) > 0;
          this.arrScholarshipA[index].rateShow = rateShow;
          this.arrScholarshipA[index].wholeCourseFee = hasSnapshot
            ? (+reFormatCurrent(snapshotFee)).toLocaleString('vi-VN')
            : currentFee;
          // Biểu phí đã đổi kể từ lúc duyệt -> nói rõ để người duyệt không tưởng màn hình hiện sai
          this.arrScholarshipA[index].feeChangedNote = (hasSnapshot && currentFee && reFormatCurrent(currentFee) !== +reFormatCurrent(snapshotFee))
            ? `Biểu phí hiện tại của ngành này là ${currentFee} — số bên trên là học phí tại thời điểm duyệt.`
            : '';
        });
      } else {
        this.arrScholarshipA = [
          { issuedScholarshipId: "", issuedScholarshipValue: "", programtype: "", issuedMajorId: "" },
        ];
      }
      if (record.status == 'pending') {
        setTimeout(() => {
          this.dataCustomer.status = 'approved';
        }, 200);
      }
      this.handleChangeStatus();
    },
    async getAdmissionMajors(studentId: any) {
      // Lấy ngành ứng viên ĐÃ đăng ký tuyển sinh (mỗi ngành kèm admissionRegisterId + MHS) cho dropdown "Ngành nhận HB".
      this.admissionMajorOptions = [];
      if (!studentId) return;
      await axios.get(`/admission/register/student/${studentId}`)
        .then(({ data }) => {
          const list: any[] = [];
          (data || []).forEach((adReg: any) => {
            (adReg.majors || []).forEach((m: any) => {
              list.push({ majorId: m.id, majorCode: m.code, majorName: m.name, admissionRegisterId: adReg.id, admissionRegisterCode: adReg.profileCode, idAdMethod: adReg.idAdMethod });
            });
          });
          this.admissionMajorOptions = list;
        })
        .catch(() => { this.admissionMajorOptions = []; });
    },
    handleChangeStatus() {
      if (this.dataCustomer.status == 'approved') {
        this.rules = {
          issuedScholarshipId: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
          status: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
        };
        // Số/Ngày thông báo + Đợt xét nằm ở VÙNG 2 (quyền scholarship/APPROVE) — chỉ require khi user THẤY vùng đó;
        // user không có quyền Lưu = gửi chọn học bổng ở trạng thái pending, không có các field này để điền.
        if (this.canApprove) {
          this.rules.issuedNo = [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }];
          this.rules.issuedPhase = [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }];
          this.rules.issuedDate = [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }];
          this.rules.majorId = [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }];
          this.rules.majorName = [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }];
        }
      } else {
        this.rules = {
          status: [{ required: true, message: "Vui lòng điền trường này", trigger: "change" }],
        };
      }
    },
    addNewRow() {
      this.arrScholarshipA.push({
        issuedScholarshipId: "",
        issuedMajorId: "",
        issuedScholarshipValue: "",
        programtype: "",
      });
    },
    removeRowScholar(index: number) {
      if (this.arrScholarshipA.length > 1) {
        this.arrScholarshipA = this.arrScholarshipA.filter((i, ind) => ind != index);
      }
    },
    selectChangeScholarShip(event: any, index: number) {
      let record = this.scholarshipOptions.find(i => i.id == event) as any;
      if (record) {
        if (index == 0) {
          this.dataCustomer.issuedScholarshipId = record.id;
        }
        this.arrScholarshipA[index].issuedMajorId = '';
        this.arrScholarshipA[index].issuedScholarshipCode = record.code;
        this.arrScholarshipA[index].issuedScholarshipValue = (+record.value);
        this.arrScholarshipA[index].programtype = record.programtype;
        this.arrScholarshipA[index].rateShow = record.rate;
        this.arrScholarshipA[index].rate = record.rate;
        // FIX: wholeCourseFee là chuỗi đã format ("349.800.000") -> nhân thẳng ra NaN và ghi "NaN" xuống grant.
        // Phải bỏ dấu chấm trước khi nhân, y như nhánh selectChangeMajor.
        const fee = reFormatCurrent(this.arrScholarshipA[index].wholeCourseFee);
        if (fee > 0) {
          this.arrScholarshipA[index].issuedScholarshipValue = (fee * record.rate).toFixed(0);
        }
      }
    },
    selectChangeMajor(event: any, index: number) {
      let record = this.majorOptions.find(i => i.id == event) as any;
      if (index == 0) {
        this.dataCustomer.issuedMajorId = event;
      }
      if (this.arrScholarshipA[index]) {
        this.arrScholarshipA[index].wholeCourseFee = record.wholeCourseFee;
        this.arrScholarshipA[index].issuedScholarshipValue = (reFormatCurrent(record.wholeCourseFee) * this.arrScholarshipA[index].rate).toFixed(0);
        // Nối hồ sơ tuyển sinh (admissionRegister) của đúng ngành được chọn — khớp theo mã ngành
        let adMajor = this.admissionMajorOptions.find((a: any) => a.majorCode == record?.majorCode) as any;
        if (adMajor) {
          this.arrScholarshipA[index].admissionRegisterId = adMajor.admissionRegisterId;
          this.arrScholarshipA[index].admissionRegisterCode = adMajor.admissionRegisterCode;
          this.arrScholarshipA[index].adMajorId = adMajor.majorId; // Major.id (để set scholarship_register.majorId, khác tuition.id)
        }
      }
    },
    approvedFunction() {
      if (this.saving) return; // chặn double-click ngay ở trigger (belt-and-suspenders với guard trong submit)
      // Từ chối PHẢI có lý do (truy vết — drawer/DS đọc lại otherInfo.rejectReason; bỏ trống là mất dấu vì sao từ chối)
      if (this.dataCustomer.status === 'rejected' && !(this.dataCustomer.rejectReason || '').trim()) {
        Swal.fire({
          text: 'Vui lòng nhập lý do từ chối.',
          icon: 'warning',
          buttonsStyling: false,
          confirmButtonText: 'Đóng',
          customClass: { confirmButton: 'btn btn-primary' },
        });
        return;
      }
      // Chặn sớm: ứng viên KHÔNG có ngành/hồ sơ tuyển sinh nào (admissionMajorOptions rỗng) thì không cấp được
      // (grant lấy admissionRegisterId từ ngành được chọn) -> báo rõ thay vì để BE trả 400 thô.
      if (this.dataCustomer.status === 'approved' && (!this.admissionMajorOptions || this.admissionMajorOptions.length === 0)) {
        Swal.fire({
          text: 'Ứng viên chưa có hồ sơ tuyển sinh (ngành) nào nên không thể cấp học bổng.',
          icon: 'warning',
          buttonsStyling: false,
          confirmButtonText: 'Đóng',
          customClass: { confirmButton: 'btn btn-primary' },
        });
        return;
      }
      // Ngành chưa cài học phí (cột học phí toàn khóa = 0/trống) -> giá trị học bổng tính ra 0đ.
      // Trước đây vẫn cấp được, grant lưu 0 và không ai biết cho tới lúc in phiếu.
      if (this.dataCustomer.status === 'approved') {
        const noFee = this.arrScholarshipA
          .filter((i: any) => i.issuedScholarshipId && i.issuedMajorId && !this.hasFee(i))
          .map((i: any) => this.majorOptions.find((m: any) => m.id == i.issuedMajorId)?.name || i.issuedMajorId);
        if (noFee.length) {
          Swal.fire({
            title: 'Ngành chưa cài học phí',
            html: `Chưa có học phí toàn khóa cho: <b>${noFee.join(', ')}</b>.<br><br>Cấp học bổng lúc này sẽ ra <b>0đ</b>. Vui lòng cài học phí ở <b>Cài đặt thủ tục nhập học → Học phí</b> rồi duyệt lại.`,
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: 'Đã hiểu',
            customClass: { confirmButton: 'btn btn-primary' },
          });
          return;
        }
      }
      this.submit(true);
    },
    // Dòng cấp đã có học phí toàn khóa hợp lệ (> 0) chưa — dùng cho cả cảnh báo hiển thị lẫn chặn duyệt
    hasFee(row: any) {
      return reFormatCurrent(row?.wholeCourseFee) > 0;
    },
    async submit(isAdmin?: any) {
      const formEl = this.$refs.formRef as any;
      if (!formEl) return;
      if (this.saving) return; // đang xử lý -> chặn cú bấm thứ 2 (tránh cấp học bổng trùng)
      this.saving = true;
      await formEl.validate(async (valid, fields) => {
       try {
        let recordScholarship = this.scholarshipOptions.find((item) => item.id == this.dataCustomer.issuedScholarshipId) || {} as any;
        let dataT = this.dataCustomer as any;
        if (valid) {
          // Dòng cấp đầu tiên: nguồn cho các field issued*/major* lưu NGAY trên register
          // (trước đây không lưu tên -> drawer chi tiết hiện '—' phải fallback từ grant)
          const first = this.arrScholarshipA[0] || {} as any;
          const firstScholarship = this.scholarshipOptions.find((i) => i.id == first.issuedScholarshipId) || {} as any;
          const firstMajor = this.majorOptions.find((i) => i.id == first.issuedMajorId) || {} as any;
          let data = {
            issuedDate: dataT.issuedDate,
            issuedNo: dataT.issuedNo,
            issuedPhase: +dataT.issuedPhase,
            otherInfo: this.arrScholarshipA.map((i) => ({ ...i, wholeCourseFee: reFormatCurrent(i.wholeCourseFee) })),
            status: isAdmin ? dataT.status : 'pending',
            issuedScholarshipId: first.issuedScholarshipId || dataT.issuedScholarshipId,
            issuedScholarshipCode: first.issuedScholarshipCode || dataT.issuedScholarshipCode,
            issuedScholarshipName: firstScholarship.name || dataT.issuedScholarshipName,
            issuedProgramtype: firstScholarship.programtype || dataT.issuedProgramtype,
            // majorId + admissionRegisterId của bản ghi ĐK: lấy từ ngành được cấp (dòng grant đầu) -> qua check 'approved' của BE
            majorId: (first && first.adMajorId) || dataT.majorId,
            majorName: firstMajor.majorName || dataT.majorName,
            majorCode: firstMajor.majorCode || dataT.majorCode,
            admissionRegisterId: (first && first.admissionRegisterId) || dataT.admissionRegisterId,
            admissionRegisterCode: (first && first.admissionRegisterCode) || dataT.admissionRegisterCode,
          } as any;
          let dataCreatemulti = [] as any;
          let isReturn = false;
          if (isAdmin) {
            if (dataT.status != 'approved') {
              // Từ chối: chỉ cập nhật status; có nhập lý do thì lưu vào otherInfo (object). Không tạo grant, không đẩy SF.
              data = { status: dataT.status };
              if (dataT.rejectReason) data.otherInfo = { rejectReason: dataT.rejectReason };
            } else {
              this.arrScholarshipA.filter((i: any) => i.issuedScholarshipId && i.issuedMajorId).forEach((item: any) => {
                let recordScholarship = this.scholarshipOptions.find((i) => i.id == item.issuedScholarshipId) || {} as any;
                let recordMajor = this.majorOptions.find((i) => i.id == item.issuedMajorId) || {} as any;
                let scholarshipValue = (+item.issuedScholarshipValue).toFixed(0);
                let temp = {
                  "scholarshipRegisterId": dataT.id,
                  "admissionRegisterId": item.admissionRegisterId || dataT.admissionRegisterId,
                  "admissionRegisterCode": item.admissionRegisterCode || dataT.admissionRegisterCode,
                  "studentId": dataT.studentId,
                  "majorId": recordMajor.id,
                  "majorCode": recordMajor.majorCode,
                  "scholarshipId": recordScholarship.id,
                  "scholarshipCode": recordScholarship.code,
                  "scholarshipType": recordScholarship.type,
                  "scholarshipValue": scholarshipValue,
                  "programtype": recordScholarship.programtype,
                  "rate": recordScholarship.rate,
                  "releasedIn": recordScholarship.releasedIn,
                  "issuedNo": dataT.issuedNo,
                  "issuedDate": dataT.issuedDate,
                  "issuedPhase": +dataT.issuedPhase,
                  "description": recordScholarship.description,
                };
                dataCreatemulti.push(temp);
              });
              await axios.post(`scholarship/student/createmulti`, dataCreatemulti)
                .then(() => { /* grant created */ })
                .catch(({ response }) => {
                  console.log('err', response);
                  Swal.fire({
                    text: response.data?.errorMessage || "BadRequestException",
                    icon: 'error',
                    buttonsStyling: false,
                    confirmButtonText: "Đóng",
                    customClass: { confirmButton: "btn btn-primary" },
                  });
                  isReturn = true;
                });
            }
          }
          if (this.isMultiApproved) {
            // bulk approve: luồng updatemulti đang tắt (giữ tương thích hành vi trang DS cũ)
            return;
          }
          if (isReturn) {
            return;
          }
          await axios.put(`/scholarshipregisters/${dataT.id}`, data)
            .then(() => {
              let text = dataT.status == 'approved' ? 'duyệt' : 'từ chối';
              let a = `Đã ${text} cấp học bổng thành công!`;
              if (!isAdmin) {
                a = 'Đã lưu thành công!';
              }
              if (dataT.status == 'approved' && dataT.opportunityid) {
                axios.patch(`/salesforces/sobject/opportunities/${dataT.opportunityid}`, { "Scholarship_Allocation__c": recordScholarship.code });
              }
              Swal.fire({
                text: a,
                icon: "success",
                buttonsStyling: false,
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
              });
              this.hide();
              this.$emit('saved');
            })
            .catch(({ response }) => {
              console.log('err', response);
            });
        } else {
          console.log('error submit!', fields);
        }
       } finally {
        this.saving = false; // mở khoá mọi nhánh (valid/invalid/early-return/lỗi)
       }
      });
    },
  },
});
